/**
 * Vizualno stanje napak za obvezna polja v formah z razredom .required-error.
 *
 * - .field-required-error na neveljavnem polju (rdeč rob) in pri radio/checkbox tudi na
 *   ovoju .filter-input ter na skupini [data-required-group] (nativni input je skrit,
 *   zato mora rdeče postati lasten krogec; brskalnikov balonček se pri skritih poljih ne prikaže).
 * - .required-error-show-note: opomba pod poljem/skupino ("To polje je obvezno" ali
 *   data-required-message na sidru [data-required-group] / [data-required-anchor]).
 * - .required-error-show-summary: prikaže .form-response.error; če je prazen, vanj zapiše
 *   data-required-summary forme ali privzeto besedilo.
 * - ob kliku na oddajo zdrsne do prvega neveljavnega polja.
 */
export default {
	init() {
		const forms = document.querySelectorAll('form.required-error')
		if (!forms.length) return

		const DEFAULT_NOTE = 'To polje je obvezno'
		const DEFAULT_SUMMARY = 'Za nadaljevanje izpolnite vsa obvezna polja, označena z zvezdico in obarvana rdeče.'

		function isChoice(el) {
			return !!el && el.tagName === 'INPUT' && (el.type === 'radio' || el.type === 'checkbox')
		}

		function getErrorAnchor(el) {
			if (!el || !el.closest) return el
			return el.closest('[data-required-group], [data-required-anchor]')
				|| el.closest('.mb-4') || el.closest('.mb-2') || el.closest('.filter-input') || el.parentElement || el
		}

		// Elementi, ki poleg samega polja dobijo .field-required-error (za stiliziranje lastnih krogcev)
		function getWrappers(el) {
			const list = []
			if (!el || !el.closest) return list
			if (isChoice(el)) {
				const wrap = el.closest('.filter-input')
				if (wrap) list.push(wrap)
				// Skupino označi samo izbira (radio/checkbox), ne druga polja, ki so gnezdena v njej
				const group = el.closest('[data-required-group]')
				if (group) list.push(group)
			}
			return list
		}

		function setRequiredNote(el, form, show) {
			if (!form || !form.classList.contains('required-error-show-note')) return
			const anchor = getErrorAnchor(el)
			if (!anchor) return
			let note = anchor.querySelector(':scope > .tr-required-error')
			if (!note) {
				if (!show) return // ne ustvarjaj skritih opomb za veljavna polja
				note = document.createElement('div')
				note.className = 'tr-required-error text-red text-[12px] mt-1 hidden'
				note.textContent = anchor.dataset.requiredMessage || DEFAULT_NOTE
				anchor.appendChild(note)
			}
			note.classList.toggle('hidden', !show)
		}

		function setSummaryError(form, show) {
			if (!form || !form.classList.contains('required-error-show-summary')) return
			const summary = form.querySelector('.form-response.error')
			if (!summary) return
			if (show) {
				// Besedilo vstavimo samo v prazen povzetek (ali v tisti, ki smo ga sami napolnili),
				// da ne povozimo oblikovanih povzetkov drugih form ali sporočil strežnika.
				if (!summary.textContent.trim() || summary.dataset.requiredSummaryOwned === '1') {
					summary.textContent = form.dataset.requiredSummary || DEFAULT_SUMMARY
					summary.dataset.requiredSummaryOwned = '1'
				}
				summary.classList.remove('hidden')
			} else {
				summary.classList.add('hidden')
			}
		}

		function markInvalid(el, form) {
			if (!el) return
			el.classList.add('field-required-error')
			getWrappers(el).forEach(w => w.classList.add('field-required-error'))
			setRequiredNote(el, form, true)
		}

		// Tiho preverjanje: checkValidity() sproži dogodek "invalid" (in s tem rdečo oznako), validity.valid ne.
		function isValid(el) {
			return !el.willValidate || (el.validity ? el.validity.valid : el.checkValidity())
		}

		function unmark(el, form) {
			if (!el) return
			el.classList.remove('field-required-error')
			getWrappers(el).forEach(w => w.classList.remove('field-required-error'))
			// Opomba pripada sidru (npr. vrstici dneva z dvema urama): skrij jo šele, ko v sidru ni več označenega polja
			const anchor = getErrorAnchor(el)
			const stillInvalid = anchor && anchor.querySelector ? anchor.querySelector('input.field-required-error, select.field-required-error, textarea.field-required-error') : null
			if (!stillInvalid) setRequiredNote(el, form, false)
		}

		// Naš povzetek skrijemo, ko ni več nobenega označenega polja (sporočil strežnika ne diramo)
		function hideOwnedSummaryIfClean(form) {
			const summary = form.querySelector('.form-response.error')
			if (!summary || summary.dataset.requiredSummaryOwned !== '1') return
			if (!form.querySelector('.field-required-error')) summary.classList.add('hidden')
		}

		function unmarkIfValid(el, form) {
			if (!el || !el.checkValidity) return
			// Radio skupina: ob izbiri postanejo veljavni vsi gumbi z istim imenom
			if (isChoice(el) && el.type === 'radio' && el.name) {
				const name = el.name.replace(/"/g, '\\"')
				form.querySelectorAll('input[type="radio"][name="' + name + '"]').forEach(r => {
					if (isValid(r)) unmark(r, form)
				})
			} else if (isValid(el)) {
				unmark(el, form)
			}
			// Sprememba lahko vpliva na sosednja polja v istem sidru (npr. "zaprto" onemogoči urni polji): počisti veljavna
			const container = el.closest ? el.closest('[data-required-anchor], [data-required-group]') : null
			if (container) {
				container.querySelectorAll('input, textarea, select').forEach(c => {
					if (c !== el && isValid(c)) unmark(c, form)
				})
			}
			hideOwnedSummaryIfClean(form)
		}

		function scrollToFirstInvalid(ctrl) {
			const target = getErrorAnchor(ctrl) || ctrl
			try {
				target.scrollIntoView({ behavior: 'smooth', block: 'center' })
			} catch (e) {
				target.scrollIntoView()
			}
			if (!isChoice(ctrl) && typeof ctrl.focus === 'function') {
				try { ctrl.focus({ preventScroll: true }) } catch (e) { /* ignore */ }
			}
		}

		forms.forEach(form => {
			function runValidationAndSummary(scroll) {
				const controls = form.querySelectorAll('input, textarea, select')
				let firstInvalid = null
				controls.forEach(ctrl => {
					if (ctrl.willValidate && !ctrl.checkValidity()) {
						markInvalid(ctrl, form)
						if (!firstInvalid) firstInvalid = ctrl
					} else {
						unmark(ctrl, form)
					}
				})
				setSummaryError(form, !!firstInvalid)
				if (firstInvalid && scroll) scrollToFirstInvalid(firstInvalid)
				return !firstInvalid
			}

			// On submit, sync visual state with constraint validation
			form.addEventListener('submit', () => {
				runValidationAndSummary(false)
			})

			// When browser flags a field invalid during submission
			form.addEventListener('invalid', (e) => {
				markInvalid(e.target, form)
			}, true)

			// On submit-button click, show errors, summary and scroll to the first missing field
			form.addEventListener('click', (e) => {
				const btn = e.target && e.target.closest ? e.target.closest('button[type="submit"], input[type="submit"]') : null
				if (!btn) return
				runValidationAndSummary(true)
			})

			// While typing/changing, clear error state when valid
			form.addEventListener('input', (e) => unmarkIfValid(e.target, form))
			form.addEventListener('change', (e) => unmarkIfValid(e.target, form))
		})
	}
}
